import { createContext, useContext, useState, useEffect } from 'react';
import { Lead, Note } from '@/types/lead';
import {
  fetchLeads,
  fetchLeadById,
  createLead,
  updateLead as updateLeadApi,
  deleteLead as deleteLeadApi,
  addLeadNote,
  addLeadTag,
  removeLeadTag,
  bulkUploadLeads,
  searchLeads
} from '@/lib/leadApi';
import { parseCSV } from '@/lib/csvParser';
import { useOrganization } from './ClerkOrganizationContext';
import { useUser } from '@clerk/clerk-react';

interface LeadContextType {
  leads: Lead[];
  loading: boolean;
  error: string | null;
  getLeadById: (id: string) => Lead | undefined;
  addLead: (lead: Omit<Lead, 'id' | 'createdAt' | 'notes' | 'tags'>) => Promise<void>;
  updateLead: (id: string, data: Partial<Lead>) => Promise<void>;
  deleteLead: (id: string) => Promise<void>;
  addNote: (leadId: string, note: string) => Promise<void>;
  addTag: (leadId: string, tag: string, color?: string) => Promise<void>;
  removeTag: (leadId: string, tag: string) => Promise<void>;
  bulkImportLeads: (leads: any[]) => Promise<{ successful: number; failed: number; errors: string[] }>;
  searchLeads: (searchTerm: string) => Promise<Partial<Lead>[]>;
  refreshLeads: () => Promise<void>;
}

const LeadContext = createContext<LeadContextType | undefined>(undefined);

export const LeadProvider = ({ children }: { children: React.ReactNode }) => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentOrganization } = useOrganization();
  const { user } = useUser();

  // Fetch leads when organization changes
  useEffect(() => {
    if (currentOrganization) {
      loadLeads();
    } else {
      setLeads([]);
      setLoading(false);
    }
  }, [currentOrganization]);

  const loadLeads = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if we have a current organization
      if (!currentOrganization?.id) {
        setLeads([]);
        return;
      }
      
      const fetchedLeads = await fetchLeads(user?.id, currentOrganization?.id);
      setLeads(fetchedLeads);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch leads';
      
      // Handle the case where organization hasn't been synced yet
      if (errorMessage.includes('Organization not synced yet')) {
        setError('Setting up your organization... Please wait a moment.');
        setLeads([]);
        
        // Retry after a short delay to allow sync to complete
        setTimeout(() => {
          if (currentOrganization?.id) {
            loadLeads();
          }
        }, 2000);
      } else {
        setError(errorMessage);
        // console.error('Error fetching leads:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const getLeadById = (id: string) => {
    return leads.find((lead) => lead.id === id);
  };

  const addLead = async (lead: Omit<Lead, 'id' | 'createdAt' | 'notes' | 'tags'>) => {
    try {
      setError(null);
      
      // Validate we have the required Clerk data
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      if (!currentOrganization?.id) {
        throw new Error('No organization selected');
      }
      
      const newLead = await createLead(lead, user.id, currentOrganization.id);
      setLeads((prev) => [newLead, ...prev]);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create lead';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const updateLead = async (id: string, data: Partial<Lead>) => {
    try {
      setError(null);
      
      // Validate we have the required Clerk data
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      if (!currentOrganization?.id) {
        throw new Error('No organization selected');
      }
      
      const updatedLead = await updateLeadApi(id, data, user.id, currentOrganization.id);
      setLeads((prev) =>
        prev.map((lead) => (lead.id === id ? updatedLead : lead))
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update lead';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const deleteLead = async (id: string) => {
    try {
      setError(null);
      
      // Validate we have the required Clerk data
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      if (!currentOrganization?.id) {
        throw new Error('No organization selected');
      }
      
      await deleteLeadApi(id, user.id, currentOrganization.id);
      setLeads((prev) => prev.filter((lead) => lead.id !== id));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete lead';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const addNote = async (leadId: string, noteContent: string) => {
    try {
      setError(null);
      const newNote = await addLeadNote(leadId, noteContent, user?.id);
      setLeads((prev) =>
        prev.map((lead) => {
          if (lead.id === leadId) {
            return {
              ...lead,
              notes: [...lead.notes, newNote],
            };
          }
          return lead;
        })
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add note';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const addTag = async (leadId: string, tag: string, color: string = '#6b7280') => {
    try {
      setError(null);
      await addLeadTag(leadId, tag, color);
      setLeads((prev) =>
        prev.map((lead) => {
          if (lead.id === leadId && !lead.tags.includes(tag)) {
            return {
              ...lead,
              tags: [...lead.tags, tag],
              tagColors: [...(lead.tagColors || []), { tag, color }],
            };
          }
          return lead;
        })
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add tag';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const removeTag = async (leadId: string, tag: string) => {
    try {
      setError(null);
      await removeLeadTag(leadId, tag);
      setLeads((prev) =>
        prev.map((lead) => {
          if (lead.id === leadId) {
            return {
              ...lead,
              tags: lead.tags.filter((t) => t !== tag),
              tagColors: lead.tagColors?.filter((tc) => tc.tag !== tag) || [],
            };
          }
          return lead;
        })
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove tag';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const bulkImportLeads = async (leads: any[]): Promise<{ successful: number; failed: number; errors: string[] }> => {
    try {
      setError(null);
      
      // Upload leads using the API
      const uploadResult = await bulkUploadLeads(leads);
      
      // Refresh leads to show new data
      await loadLeads();
      
      return uploadResult;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to import leads';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const searchLeadsApi = async (searchTerm: string): Promise<Partial<Lead>[]> => {
    try {
      setError(null);
      return await searchLeads(searchTerm);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to search leads';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const refreshLeads = async () => {
    await loadLeads();
  };

  return (
    <LeadContext.Provider
      value={{
        leads,
        loading,
        error,
        getLeadById,
        addLead,
        updateLead,
        deleteLead,
        addNote,
        addTag,
        removeTag,
        bulkImportLeads,
        searchLeads: searchLeadsApi,
        refreshLeads,
      }}
    >
      {children}
    </LeadContext.Provider>
  );
};

export const useLeads = () => {
  const context = useContext(LeadContext);
  if (context === undefined) {
    throw new Error('useLeads must be used within a LeadProvider');
  }
  return context;
};